// SPDX-License-Identifier: AGPL-3.0-or-later

import * as ThemePreferenceCommands from '@app/features/theme/commands/ThemePreferenceCommands';
import Theme from '@app/features/theme/state/Theme';
import {Button} from '@app/features/ui/button/Button';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Switch} from '@app/features/ui/components/form/Switch';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import styles from '@app/features/user/components/modals/tabs/appearance_tab/ThemeTab.module.css';
import type {ThemeType} from '@app/vendor/constants/UserConstants';
import {ThemeTypes} from '@app/vendor/constants/UserConstants';
import {useLingui} from '@lingui/react/macro';
import {ArrowsCounterClockwiseIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {CustomizeThemeModal} from './CustomizeThemeModal';
import {ThemeButton} from './ThemeButton';
export const ThemeTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const syncAcrossDevices = Theme.syncAcrossDevices;
	const themePreference = Theme.themePreference;
	const currentSelectedTheme: ThemeType = themePreference;
	const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
		return window.matchMedia?.('(prefers-color-scheme: dark)').matches;
	});
	const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
	const themeToFocusRef = useRef<string | null>(null);
	useEffect(() => {
		if (!window.matchMedia) return;
		const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = (event: MediaQueryListEvent) => {
			setSystemPrefersDark(event.matches);
		};
		mediaQuery.addEventListener('change', handleChange);
		return () => mediaQuery.removeEventListener('change', handleChange);
	}, []);
	const handleThemeChange = useCallback(
		(newTheme: ThemeType) => {
			if (newTheme === currentSelectedTheme) return;
			themeToFocusRef.current = newTheme;
			ThemePreferenceCommands.updateThemePreference(newTheme);
		},
		[currentSelectedTheme],
	);
	const themeOptions = useMemo(
		() =>
			[
				{
					type: ThemeTypes.DARK,
					label: t`Dark Theme`,
					backgroundColor: 'hsl(258, calc(10% * var(--saturation-factor)), 12.04%)',
					isLight: false,
					icon: null,
					tooltip: t`Use dark theme`,
				},
				{
					type: ThemeTypes.DARK_LEGACY,
					label: t`Dark (Legacy) Theme`,
					backgroundColor: 'hsl(220, calc(13% * var(--saturation-factor)), 11.18%)',
					isLight: false,
					icon: null,
					tooltip: t`Use legacy dark theme (original neutral grey palette)`,
				},
				{
					type: ThemeTypes.COAL,
					label: t`Coal Theme`,
					backgroundColor: 'hsl(258, 10%, 2%)',
					isLight: false,
					icon: null,
					tooltip: t`Use coal theme (pitch-black surfaces)`,
				},
				{
					type: ThemeTypes.LIGHT,
					label: t`Light Theme`,
					backgroundColor: 'hsl(220, 10%, 98.5%)',
					isLight: true,
					icon: null,
					tooltip: t`Use light theme`,
				},
				{
					type: ThemeTypes.SYSTEM,
					label: t`System Theme`,
					backgroundColor: systemPrefersDark
						? 'hsl(258, calc(10% * var(--saturation-factor)), 5%)'
						: 'hsl(220, 10%, 98.5%)',
					isLight: !systemPrefersDark,
					icon: <ArrowsCounterClockwiseIcon size={12} />,
					tooltip: systemPrefersDark
						? t`System: Dark theme (automatically sync with your system's dark/light preference)`
						: t`System: Light theme (automatically sync with your system's dark/light preference)`,
				},
			] satisfies ReadonlyArray<{
				type: ThemeType;
				label: string;
				backgroundColor: string;
				isLight: boolean;
				icon: React.ReactNode | null;
				tooltip: string;
			}>,
		[systemPrefersDark, t],
	);
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent, targetTheme: ThemeType) => {
			if (event.key === ' ' || event.key === 'Enter') {
				event.preventDefault();
				handleThemeChange(targetTheme);
			} else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
				event.preventDefault();
				const order = themeOptions.map((option) => option.type);
				const direction = event.key === 'ArrowRight' ? 1 : -1;
				const currentIndex = Math.max(order.indexOf(currentSelectedTheme as (typeof order)[number]), 0);
				const nextIndex = (currentIndex + direction + order.length) % order.length;
				const nextTheme = order[nextIndex];
				if (nextTheme) {
					handleThemeChange(nextTheme);
				}
			}
		},
		[themeOptions, currentSelectedTheme, handleThemeChange],
	);
	useEffect(() => {
		if (themeToFocusRef.current) {
			const node = buttonRefs.current[themeToFocusRef.current];
			if (node) {
				node.focus();
			}
			themeToFocusRef.current = null;
		}
	}, [currentSelectedTheme]);
	const handleOpenCustomize = useCallback(() => {
		ModalCommands.push(modal(() => <CustomizeThemeModal />));
	}, []);
	return (
		<>
			<div className={styles.themeButtonGroup} role="radiogroup" aria-label={t`Theme`}>
				{themeOptions.map((option) => (
					<Tooltip key={option.type} text={option.tooltip} position="top" delay={200}>
						<div>
							<ThemeButton
								ref={(el) => {
									buttonRefs.current[option.type] = el;
								}}
								themeType={option.type}
								currentTheme={currentSelectedTheme}
								label={option.label}
								backgroundColor={option.backgroundColor}
								isLight={option.isLight}
								icon={option.icon ?? undefined}
								onClick={handleThemeChange}
								onKeyDown={handleKeyDown}
							/>
						</div>
					</Tooltip>
				))}
			</div>
			<Switch
				label={t`Sync theme across devices`}
				description={
					themePreference === ThemeTypes.SYSTEM
						? t`System theme automatically disables sync to track your system's preference on this device.`
						: t`When enabled, theme changes will sync to all your devices. When disabled, this device will use its own theme setting.`
				}
				value={syncAcrossDevices}
				disabled={themePreference === ThemeTypes.SYSTEM}
				onChange={(value) => ThemePreferenceCommands.setSyncAcrossDevices(value)}
			/>
			<Button variant="secondary" fitContent onClick={handleOpenCustomize}>
				{t`Customize theme…`}
			</Button>
		</>
	);
});
